/**
 * The `logo` control kind — README §6: "two upload wells previewed on dark
 * and light." Resolution #2: the wells are real file inputs, and since
 * there is no default asset (the cat-face mark still needs a
 * dark-readable version), an empty well shows an honest empty state rather
 * than a placeholder image.
 *
 * There is no upload endpoint yet (binary assets don't fit the scalar
 * `PUT /ops/config` shape) — resolution #4: this section's own
 * "NOT WIRED" chip already says so, so previews live only in the shared
 * store's `logoDark`/`logoLight` (docs/design-handoff/README.md "State
 * management") for this session; nothing here calls the network.
 *
 * Task 35 ("control right", README §6): this root div is intentionally
 * content-sized (`flex gap-4`, no `w-full`), the same choice made for the
 * hours block in HoursField.tsx — it right-justifies as one two-well unit
 * rather than stretching to fill the control column and leaving the wells
 * pinned to the column's left edge. That comes for free from FieldRow's
 * `justify-end` control column precisely because nothing here forces this
 * div to a fixed or full width.
 */
import { useRef } from 'react'
import type { ConfigField } from '@communicator/shared'
import { useStore } from '../../store'

/**
 * Task 33: the dark and light wells share this exact size class (only the
 * background token differs) so the two wells line up with each other the
 * same way FIELD_ROW_GRID_CLASS lines up field rows — a fixed size, not one
 * that could drift if a preview image ever pushed a well wider than the
 * other.
 */
export const WELL_SIZE_CLASS = 'h-[64px] w-[104px]'

interface WellProps {
  label: 'Dark' | 'Light'
  wellBg: string
  src: string | null
  locked: boolean
  onFile: (dataUrl: string) => void
}

function Well({ label, wellBg, src, locked, onFile }: WellProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        disabled={locked}
        onClick={() => inputRef.current?.click()}
        data-testid={`logo-well-${label.toLowerCase()}`}
        className={`flex ${WELL_SIZE_CLASS} items-center justify-center rounded-[8px] border border-dashed border-subtle ${wellBg} disabled:opacity-50`}
      >
        {src ? (
          <img src={src} alt={`${label} logo preview`} className="max-h-[48px] max-w-[88px]" />
        ) : (
          <span className="px-2 text-center text-[11px] text-lo">No file · {label.toLowerCase()} UI</span>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/svg+xml,image/png"
        className="hidden"
        aria-label={`Upload ${label.toLowerCase()} logo`}
        disabled={locked}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (!file) return
          const reader = new FileReader()
          reader.onload = () => {
            if (typeof reader.result === 'string') onFile(reader.result)
          }
          reader.readAsDataURL(file)
        }}
      />
      <span className="text-[11px] text-lo">{label}</span>
    </div>
  )
}

export interface LogoFieldProps {
  field: ConfigField
  locked: boolean
}

export function LogoField({ field, locked }: LogoFieldProps) {
  const logoDark = useStore((s) => s.logoDark)
  const logoLight = useStore((s) => s.logoLight)
  const set = useStore((s) => s.set)

  return (
    <div className="flex gap-4" data-testid={`logo-${field.id}`}>
      <Well label="Dark" wellBg="bg-sidebar" src={logoDark} locked={locked} onFile={(d) => set({ logoDark: d })} />
      <Well label="Light" wellBg="bg-card" src={logoLight} locked={locked} onFile={(d) => set({ logoLight: d })} />
    </div>
  )
}
